import React from 'react';
import { useTranslation } from 'react-i18next';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';

import { stripeLocale } from '@/utils/stripe-locale';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';
import { getStripe, useStripeAppearance } from '../../../configurator-v3/data/payments';
import { Button, Icon, Text } from '../../../../components';

export type CardPaymentSheetProps = {
  /** Preformatted money the card will be charged (a data value, e.g. "$25.00 USD"). */
  amountLabel: string;
  /**
   * Mint (or fetch) the payable invoice and return its Stripe client secret. Called once on open.
   * Kept injectable so the same sheet serves any "pay this invoice by card" flow — here it mints the
   * `deposit_ai_credits` invoice.
   */
  createIntent: () => Promise<{ clientSecret: string | undefined; invoiceId: string }>;
  /** Fired the moment Stripe confirms the charge (before the server webhook credits), so the caller
   *  can start polling the balance. */
  onSuccess: () => void;
  onClose: () => void;
};

/** The Stripe Payment Element + confirm, mounted inside <Elements> bound to the invoice's intent.
 *  Mirrors the wallet DepositModal's card confirm (validate → confirmPayment, `redirect:'if_required'`),
 *  registering its confirm into `confirmRef` so the footer Pay button (outside <Elements>) triggers it. */
function CardForm(p: {
  clientSecret: string;
  confirmRef: React.MutableRefObject<(() => void) | null>;
  setPaying: (value: boolean) => void;
  setError: (value: string | null) => void;
  onPaid: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const { clientSecret, confirmRef, setPaying, setError, onPaid } = p;

  React.useEffect(() => {
    confirmRef.current = async (): Promise<void> => {
      if (!stripe || !elements) return;
      setPaying(true);
      setError(null);
      const fail = (message: string | undefined): void => {
        setError(message ?? t('Payment failed. Please try again.'));
        setPaying(false);
      };
      const submitResult = await elements.submit();
      if (submitResult.error) {
        fail(submitResult.error.message);
        return;
      }
      const result = await stripe.confirmPayment({
        elements,
        clientSecret,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.origin + window.location.pathname },
      });
      if (result.error) {
        fail(result.error.message);
        return;
      }
      // Charged. Clear busy BEFORE handing off — a busy Sheet refuses every dismissal, which would
      // freeze the success screen behind an inert backdrop.
      setPaying(false);
      onPaid();
    };
    return () => {
      confirmRef.current = null;
    };
  }, [stripe, elements, clientSecret, confirmRef, setPaying, setError, onPaid, t]);

  return <PaymentElement options={{ layout: { type: 'accordion', defaultCollapsed: false } }} />;
}

type Screen = 'starting' | 'error' | 'form' | 'success';

/**
 * Pay an already-priced invoice by card. The amount is decided by the caller (no amount entry here):
 * on open it mints the intent (`createIntent`), binds the Stripe Payment Element to it, and on a
 * confirmed charge shows a success screen. Reuses the wallet's `deposit-*` styles + the configurator
 * Stripe integration (publishable key, themed appearance, locale). The wallet's own DepositModal is
 * left untouched.
 */
export const CardPaymentSheet = ({ amountLabel, createIntent, onSuccess, onClose }: CardPaymentSheetProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const { closeHandle, dismiss } = useSheetDismiss(onClose);
  const appearance = useStripeAppearance();

  const [screen, setScreen] = React.useState<Screen>('starting');
  const [clientSecret, setClientSecret] = React.useState<string | null>(null);
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState<string | null>(null);
  const [startError, setStartError] = React.useState<string | null>(null);
  const confirmRef = React.useRef<(() => void) | null>(null);
  // Bumped by "Try again" to re-run the mint effect after a start failure.
  const [attempt, setAttempt] = React.useState(0);

  // Mint the invoice + its Stripe intent when entering `starting` (including a retry). `currency` is
  // not a dep — the amount/currency were fixed by the caller before this sheet opened.
  React.useEffect(() => {
    if (screen !== 'starting') return;
    let cancelled = false;
    setPayError(null);
    setStartError(null);
    setClientSecret(null);
    void (async () => {
      try {
        const { clientSecret: cs } = await createIntent();
        if (cancelled) return;
        if (!cs) {
          setStartError(t('Could not start checkout. Please try again.'));
          setScreen('error');
          return;
        }
        setClientSecret(cs);
        setScreen('form');
      } catch (e) {
        if (!cancelled) {
          setStartError((e as { error?: string } | undefined)?.error ?? t('Could not start checkout. Please try again.'));
          setScreen('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- createIntent is stable per open; `attempt` drives retries
  }, [screen, attempt]);

  const onPaid = React.useCallback(() => {
    onSuccess();
    setScreen('success');
  }, [onSuccess]);

  let body: React.ReactElement;
  let footer: React.ReactElement;
  switch (screen) {
    case 'success':
      body = (
        <div className="deposit-success">
          <span className="deposit-success-icon" aria-hidden="true">
            <Icon name="check" size={28} />
          </span>
          <Text tag="span" className="deposit-success-title" text="Payment successful" />
          <Text
            tag="span"
            className="deposit-success-body"
            text="{{amount}} paid. Your credits are on the way."
            placeholders={{ amount: amountLabel }}
          />
          <Text
            tag="span"
            className="deposit-success-note"
            text="It may take a few moments for your credit balance to update."
          />
        </div>
      );
      footer = <Button variant="primary" fullWidth label={t('Done')} onClick={dismiss} />;
      break;

    case 'error':
      body = (
        <div className="deposit-form">
          <div className="deposit-summary">
            <span className="deposit-summary-amount">{amountLabel}</span>
          </div>
          <span className="deposit-error" role="alert">
            {startError}
          </span>
        </div>
      );
      // Re-enter `starting` (not just bump `attempt`): the mint effect early-returns unless
      // screen==='starting', so the retry must move the screen back or it would be inert.
      footer = (
        <Button
          variant="primary"
          fullWidth
          label={t('Try again')}
          onClick={() => {
            setAttempt((n) => n + 1);
            setScreen('starting');
          }}
        />
      );
      break;

    case 'form':
      body = (
        <div className="deposit-form">
          <div className="deposit-summary">
            <Text tag="span" className="deposit-summary-label" text="You'll be charged {{amount}}" placeholders={{ amount: amountLabel }} />
          </div>
          <div className="deposit-stripe">
            {clientSecret ? (
              <Elements
                key={stripeLocale(i18n.language)}
                stripe={getStripe()}
                options={{ clientSecret, appearance, locale: stripeLocale(i18n.language) }}
              >
                <CardForm
                  clientSecret={clientSecret}
                  confirmRef={confirmRef}
                  setPaying={setPaying}
                  setError={setPayError}
                  onPaid={onPaid}
                />
              </Elements>
            ) : null}
          </div>
          {payError ? (
            <span className="deposit-error" role="alert">
              {payError}
            </span>
          ) : null}
        </div>
      );
      footer = (
        <Button
          variant="primary"
          icon="wallet"
          fullWidth
          disabled={paying}
          label={paying ? t('Processing…') : t('Pay {{amount}}', { amount: amountLabel })}
          onClick={() => void confirmRef.current?.()}
        />
      );
      break;

    case 'starting':
    default:
      body = (
        <div className="deposit-form">
          <Text tag="span" className="deposit-intro" text="Pay securely with your card. Your credits are added the moment the payment clears." />
          <div className="deposit-summary">
            <span className="deposit-summary-amount">{amountLabel}</span>
          </div>
          <Text tag="span" className="deposit-intro" text="Starting secure checkout…" />
        </div>
      );
      footer = <Button variant="primary" fullWidth disabled label={t('Pay {{amount}}', { amount: amountLabel })} />;
      break;
  }

  return (
    <Sheet
      title={t('Pay by card')}
      ariaLabel={t('Pay by card')}
      onClose={onClose}
      closeHandle={closeHandle}
      busy={paying}
      animateResize
      footer={footer}
    >
      {body}
    </Sheet>
  );
};

export default CardPaymentSheet;
